import { type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Collections, ShortUrl } from '@air/api';
import { ShortUrlObjectType, type ShortUrlResponse } from '@air/api/types';
import { FIVE_MINUTES } from '@air/utilities';
import { type ApiResponse, callAirApi } from '@air/utils-api';

import { isWorkspaceAgentShowcaseMonikerUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentShowcaseMonikerUnresolvableError';
import { toWorkspaceAgentShowcaseMonikerSubject } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentShowcaseMonikerSubject';

const PILL_SHOWCASE_TRANSIENT_RETRIES = 1;

export const PRIVATE_WORKSPACE_AGENT_SHOWCASE_MONIKER_KEY = 'PRIVATE_WORKSPACE_AGENT_SHOWCASE_MONIKER';

export type UsePrivateWorkspaceAgentShowcaseMonikerParams = {
  collectionId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentShowcaseMonikerKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SHOWCASE_MONIKER_KEY,
  UsePrivateWorkspaceAgentShowcaseMonikerParams,
];

export const getPrivateWorkspaceAgentShowcaseMonikerKey = (
  params: UsePrivateWorkspaceAgentShowcaseMonikerParams,
): PrivateWorkspaceAgentShowcaseMonikerKey => [PRIVATE_WORKSPACE_AGENT_SHOWCASE_MONIKER_KEY, params];

export const isPrivateWorkspaceAgentShowcaseMonikerKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentShowcaseMonikerKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_SHOWCASE_MONIKER_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentShowcaseMonikerOptions = {
  /** Gated on the chip being in view, so a long history does not fan out a request per chip. */
  enabled?: boolean;
};

type CollectionGetResponse = ApiResponse<typeof Collections.get>;

/**
 * Loads title and optional public short id for a history `@[showcase:<collectionId>]` chip.
 * Collection metadata is required; a missing or disabled short url still renders in-product settings without public URL actions.
 * Preview media uses the presentation icon fallback — `Collections.get` and read-only short-url lookup do not include list-row thumbnails (design follow-up).
 */
export const fetchPrivateWorkspaceAgentShowcaseMoniker = async ({
  collectionId,
  workspaceId,
}: {
  collectionId: string;
  workspaceId: string;
}) => {
  const collection = await callAirApi<CollectionGetResponse>(() => Collections.get({ collectionId, workspaceId }));

  let shortUrl: ShortUrlResponse | null = null;

  try {
    shortUrl =
      (await callAirApi(() =>
        ShortUrl.getExistingShortIdForObject({
          workspaceId,
          id: collectionId,
          type: ShortUrlObjectType.collection,
          boardContext: undefined,
        }),
      )) ?? null;
  } catch (error) {
    if (isWorkspaceAgentShowcaseMonikerUnresolvableError(error)) {
      shortUrl = null;
    } else {
      throw error;
    }
  }

  return toWorkspaceAgentShowcaseMonikerSubject(collection, shortUrl);
};

export const usePrivateWorkspaceAgentShowcaseMoniker = (
  { collectionId, workspaceId }: UsePrivateWorkspaceAgentShowcaseMonikerParams,
  options?: UsePrivateWorkspaceAgentShowcaseMonikerOptions,
) =>
  useQuery({
    queryKey: getPrivateWorkspaceAgentShowcaseMonikerKey({ collectionId, workspaceId }),
    queryFn:
      collectionId && workspaceId
        ? () => fetchPrivateWorkspaceAgentShowcaseMoniker({ collectionId, workspaceId })
        : skipToken,
    enabled: !!collectionId && !!workspaceId && (options?.enabled ?? true),
    staleTime: FIVE_MINUTES,
    retry: (failureCount, error) =>
      !isWorkspaceAgentShowcaseMonikerUnresolvableError(error) && failureCount < PILL_SHOWCASE_TRANSIENT_RETRIES,
    refetchOnWindowFocus: (query) => !!query.state.error,
    throwOnError: false,
  });
